import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Search,
  Filter,
  Plus,
  BookOpen,
  Clock,
  MoreVertical,
  Pencil,
  Trash2,
  BadgeCheck,
} from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { ProgrammeModel } from '@/models/programme.model'
import { programmesService } from '@/services/programmesService'
import { ConfirmDeleteDialog } from '@/components/app/common/ConfirmDeleteDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Dropdown } from '@/components/ui/Dropdown'
import { cn } from '@/utils/cn'
import { Department } from '@/models/department.model'
import { departmentsService } from '@/services/departmentsService'
import { PaginationMeta } from '@/models/templates/pagination.model'
import { paths } from '@/routes/path'

type StatusFilter = 'all' | 'active' | 'inactive'

interface FeedbackBanner {
  type: 'success' | 'error'
  message: string
}

export const ProgrammesPage: React.FC = () => {
  const navigate = useNavigate()
  const [programmes, setProgrammes] = useState<ProgrammeModel[]>([])
  const [meta, setMeta] = useState<PaginationMeta | null>(null)
  const [pagination, setPagination] = useState<{ page: number; perPage: number }>({ page: 1, perPage: 12 })
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [banner, setBanner] = useState<FeedbackBanner | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false)
  const [programmeToDelete, setProgrammeToDelete] = useState<ProgrammeModel | null>(null)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const [departments, setDepartments] = useState<Department[]>([])
  const [isLoadingDepartments, setIsLoadingDepartments] = useState<boolean>(false)

  const programmeActionsDisabled = isLoadingDepartments || departments.length === 0
  const totalPages = meta ? Math.max(1, Math.ceil(meta.total / meta.perPage)) : 1

  const showBanner = (type: 'success' | 'error', message: string) => {
    setBanner({ type, message })
  }

  const loadProgrammes = useCallback(
    async (page: number = pagination.page, perPage: number = pagination.perPage) => {
      setIsLoading(true)
      try {
        const response = await programmesService.getProgrammesPage(
          { page, perPage },
          { requireAuth: true },
        )
        setProgrammes(response.data)
        setMeta(response.meta)
      } catch (error) {
        showBanner('error', (error as Error).message || 'Failed to load programmes.')
      } finally {
        setIsLoading(false)
      }
    },
    [pagination.page, pagination.perPage],
  )

  useEffect(() => {
    let mounted = true

    const fetchDepartments = async () => {
      setIsLoadingDepartments(true)
      try {
        const response = await departmentsService.getDepartmentsPage()
        if (mounted) {
          setDepartments(response.data)
        }
      } catch (error) {
        if (mounted) {
          showBanner('error', (error as Error).message || 'Failed to load departments.')
        }
      } finally {
        if (mounted) {
          setIsLoadingDepartments(false)
        }
      }
    }

    loadProgrammes(pagination.page, pagination.perPage)
    fetchDepartments()

    return () => {
      mounted = false
    }
  }, [loadProgrammes, pagination.page, pagination.perPage])

  useEffect(() => {
    if (!banner) return
    const timeout = setTimeout(() => setBanner(null), 4000)
    return () => clearTimeout(timeout)
  }, [banner])

  const filteredProgrammes = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    return programmes.filter((programme) => {
      const matchesStatus =
        statusFilter === 'all'
          ? true
          : statusFilter === 'active'
            ? programme.is_active
            : !programme.is_active

      const matchesSearch =
        term.length === 0 ||
        programme.title.toLowerCase().includes(term) ||
        (programme.description ?? '').toLowerCase().includes(term)

      return matchesStatus && matchesSearch
    })
  }, [programmes, searchTerm, statusFilter])

  const openDeleteConfirmation = (programme: ProgrammeModel) => {
    setProgrammeToDelete(programme)
    setDeleteDialogOpen(true)
  }

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setProgrammeToDelete(null)
  }

  const handleDeleteProgramme = async () => {
    if (!programmeToDelete?.id) return
    try {
      setIsDeleting(true)
      await programmesService.deleteProgramme(programmeToDelete.id)
      showBanner('success', 'Programme deleted successfully.')
      setDeleteDialogOpen(false)
      await loadProgrammes()
    } catch (error) {
      showBanner('error', (error as Error).message || 'Unable to delete programme.')
    } finally {
      setIsDeleting(false)
    }
  }

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return
    setPagination((prev) => ({ ...prev, page: newPage }))
  }

  const handlePerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = Number(event.target.value)
    setPagination({ page: 1, perPage: value })
  }


  const navigateToEdit = (programmeId: string) => {
    navigate(paths.app.programmes.edit.build(programmeId))
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 flex items-center gap-2">
            Programmes
            <BadgeCheck className="h-6 w-6 text-[#7b2a40]" />
          </h1>
          <p className="text-secondary-600 mt-1">
            Manage the programmes available to applicants.
          </p>
        </div>
        <Link to={paths.app.programmes.create.absolute}>
          <Button className="self-start">
            <Plus className="mr-2 h-4 w-4" />
            Create Programme
          </Button>
        </Link>

      </header>

      {programmeActionsDisabled && (
        <div className="rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          {isLoadingDepartments
            ? 'Loading…'
            : 'No departments available yet. Create a department before adding programmes.'}
        </div>
      )}

      {banner && (
        <div
          className={cn(
            'rounded-md border px-4 py-3 text-sm',
            banner.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-red-200 bg-red-50 text-red-700',
          )}
        >
          {banner.message}
        </div>
      )}

      <section className="flex flex-col gap-4 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400 z-10" />
          <Input
            placeholder="Search programmes…"
            className="pl-9"
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value)
              setPagination((prev) => ({ ...prev, page: 1 }))
            }}
            aria-label="Search programmes"
          />
        </div>
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value as StatusFilter)
              setPagination((prev) => ({ ...prev, page: 1 }))
            }}
            className="rounded-md border border-secondary-300 px-3 py-2 text-sm text-secondary-700 focus:outline-none focus:ring-2 focus:ring-[#7b2a40]/40"
            aria-label="Filter programmes by status"
          >
            <option value="all">All Programmes</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
          {/* <Button type="button" variant="outline" className="gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </Button> */}
          <select
            value={pagination.perPage}
            onChange={handlePerPageChange}
            className="rounded-md border border-secondary-300 px-3 py-2 text-sm text-secondary-700 focus:outline-none focus:ring-2 focus:ring-[#7b2a40]/40"
            aria-label="Items per page"
          >
            {[6, 12, 24, 50].map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
        </div>
      </section>

      {isLoading ? (
        <div className="flex justify-center py-20 text-secondary-500">Loading programmes…</div>
      ) : filteredProgrammes.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-secondary-600">
            No programmes found. {searchTerm ? 'Try a different search term.' : 'Create your first programme to get started.'}
          </CardContent>
        </Card>
      ) : (
        <section className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {filteredProgrammes.map((programme) => (
            <ProgrammeAdminCard
              key={programme.id}
              programme={programme}
              onEdit={() => programme.id && navigateToEdit(programme.id)}
              onDelete={() => openDeleteConfirmation(programme)}
            />
          ))}
        </section>
      )}

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        entityLabel="Programme"
        entityName={programmeToDelete?.title}
        onConfirm={handleDeleteProgramme}
        onClose={closeDeleteDialog}
        loading={isDeleting}
      />

      <PaginationControls
        page={pagination.page}
        totalPages={totalPages}
        meta={meta}
        onPageChange={handlePageChange}
      />
    </div>
  )
}

interface ProgrammeAdminCardProps {
  programme: ProgrammeModel
  onEdit: () => void
  onDelete: () => void
}

const ProgrammeAdminCard: React.FC<ProgrammeAdminCardProps> = ({ programme, onEdit, onDelete }) => {
  const paymentAccountCount = useMemo(() => {
    return programme.cohorts.reduce((sum, cohort) => sum + (cohort.payment_accounts?.length ?? 0), 0)
  }, [programme.cohorts])
  const updatedAt = programme.updatedAt ?? programme.updated_at ?? null

  const actionItems = [
    {
      label: 'Edit Programme',
      value: 'edit',
      icon: <Pencil className="h-4 w-4" />,
    },
    {
      label: 'Delete Programme',
      value: 'delete',
      icon: <Trash2 className="h-4 w-4" />,
    },
  ]

  const handleAction = (value: string) => {
    if (value === 'edit') {
      onEdit()
    } else if (value === 'delete') {
      onDelete()
    }
  }

  return (
    <Card className="flex h-full flex-col overflow-hidden border border-secondary-200 hover:border-[#7b2a40]/40 hover:shadow-md transition">
      <div className="relative h-36 bg-gradient-to-r from-[#7b2a40]/90 to-[#c4788a]/70">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-20" />
        <div className="absolute inset-0 flex flex-col justify-between p-4 text-white">
          <div className="flex items-start justify-between gap-2 overflow-y-hidden text-ellipsis">
            <p className="text-white text-lg text-ellipsis">
              {programme.title}
            </p>
            <Dropdown
              hideArrow
              triggerLabel={
                <span className="inline-flex items-center gap-1 rounded-md bg-white/50 px-2 py-1 text-xs font-medium backdrop-blur">
                  <MoreVertical className="h-4 w-4" />
                </span>
              }
              items={actionItems}
              onSelect={handleAction}
              align="end"
            />
          </div>
          <Badge
            className={cn(
              'border px-2 py-0.5 text-xs font-medium w-min',
              programme.is_active
                ? 'border-green-200 bg-green-100/90 hover:bg-green-100/90 text-green-800'
                : 'border-secondary-200 bg-secondary-100 hover:bg-secondary-100 text-secondary-700',
            )}
          >
            {programme.is_active ? 'Active' : 'Inactive'}
          </Badge>

        </div>
      </div>

      <CardHeader className="space-y-2">
        <CardDescription className="text-secondary-500 line-clamp-2">
          {programme.description || 'No description provided for this programme yet.'}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col gap-4">
        <dl className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="flex items-center gap-2 font-medium text-secondary-700">
              <Clock className="h-4 w-4 text-[#7b2a40]" />
              Duration
            </dt>
            <dd className="mt-1 text-secondary-500">
              {programme.duration ? `${programme.duration} ${programme.duration_unit}` : 'Not specified'}
            </dd>
          </div>
          <div>
            <dt className="flex items-center gap-2 font-medium text-secondary-700">
              <BookOpen className="h-4 w-4 text-[#7b2a40]" />
              Pay Accounts
            </dt>
            <dd className="mt-1 text-secondary-500">
              {paymentAccountCount > 0
                ? `${paymentAccountCount} account${paymentAccountCount > 1 ? 's' : ''}`
                : 'No accounts configured'}
            </dd>
          </div>
        </dl>

        <div className="mt-auto flex flex-col gap-3 pt-4">
          <div className="flex flex-wrap gap-2">
            <Button asChild>
              <Link to={programme.id ? paths.app.programmes.detail.build(programme.id) : '#'}>
                View Details
              </Link>
            </Button>
            {programme.id && (
              <Button variant="outline" asChild>
                <Link to={paths.app.programmes.cohorts.build(programme.id)}>
                  Manage Cohorts
                </Link>
              </Button>
            )}
          </div>
          <div className="text-xs text-secondary-400">
            Updated {updatedAt ? new Date(updatedAt).toLocaleDateString() : 'Recently'}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

interface PaginationControlsProps {
  page: number
  totalPages: number
  meta: PaginationMeta | null
  onPageChange: (page: number) => void
}

const PaginationControls: React.FC<PaginationControlsProps> = ({ page, totalPages, meta, onPageChange }) => {
  if (!meta) return null

  return (
    <div className="flex flex-col items-center justify-between gap-3 border-t border-secondary-200 pt-4 text-sm text-secondary-600 md:flex-row">
      <div>
        Showing page {page} of {totalPages} • Total {meta.total} programmes
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => onPageChange(page - 1)} disabled={page <= 1}>
          Previous
        </Button>
        <Button variant="outline" size="sm" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>
          Next
        </Button>
      </div>
    </div>
  )
}

export default ProgrammesPage
